import React, {useEffect, useMemo, useState} from "react";
import {Sidebar} from "primereact/sidebar";
import {Checkbox} from "primereact/checkbox";
import {Dialog} from "primereact/dialog";
import {InputTextarea} from "primereact/inputtextarea";
import {confirmDialog} from "primereact/confirmdialog";
import Icon from "../../../system/components/icon";
import Avatar from "../../../system/components/avatar";
import {RichTextEditor, RichTextView} from "../../../system/components/rich-text";
import {MEETING_STATUS, doneShare, faDate, faDigits, faNum, faShortDate, isUnresolved, repeatedTopics, topicKey} from "./meetings-labels";
import {AvatarStack, Chain, LocationText, PersonInline, Progress, SectionHead, StatusPill, personOf} from "./meetings-ui";

// «پرونده‌ی جلسه» (spec 5.3): status and time, invitees and attendance, preparation of an upcoming meeting, agenda,
// minutes, and the decisions and tasks that came out of it (or are followed up in it).
// `standalone` (the sidebar opened from a meeting card in «کارها», docs/tasks/30-meeting-cards.md): the same file for
// someone who is invited, the owner or the creator — without the project's preparation and output parts (those need
// the project); `brief` is the short list of the decisions made in it, `project` where it belongs.

const drawerPt = {root: {style: {width: "min(46rem, 100vw)"}}, content: {className: "p-0"}};
const STATUS_ACTIONS = [
    {status: "held", label: "برگزار شد", icon: "event_available"},
    {status: "not_held", label: "برگزار نشد", icon: "event_repeat", ask: "چرا جلسه برگزار نشد؟"},
    {status: "cancelled", label: "لغو شد", icon: "event_busy", ask: "چرا جلسه لغو شد؟"},
    {status: "scheduled", label: "در پیش", icon: "event_upcoming"},
];

export default function MeetingDrawer({meeting, tab, visible, onHide, actions, standalone = false, brief = [], project = null}) {
    const [editingNotes, setEditingNotes] = useState(false);
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState("");
    // «برگزار نشد» / «لغو شد» ask for the reason first (boss's feedback 2026-10-25)
    const [reasonFor, setReasonFor] = useState(null);
    const [reason, setReason] = useState("");

    useEffect(() => {
        setEditingNotes(false);
        setNotes(meeting?.notes || "");
    }, [meeting?.key, meeting?.notes]);

    const decisions = useMemo(() => (meeting ? tab.decisions.filter((decision) => meeting.decisionKeys.includes(decision.key)) : []), [meeting, tab.decisions]);
    const unresolved = useMemo(() => tab.decisions.filter(isUnresolved), [tab.decisions]);
    const repeated = useMemo(() => (meeting ? repeatedTopics(tab.meetings, meeting.agenda) : []), [meeting, tab.meetings]);
    if (!meeting) return null;

    const held = meeting.status === "held";
    const canEdit = meeting.canEdit;
    const run = async (name, work) => {
        setBusy(name);
        try {
            await work();
        } catch (error) {
            // the tab already showed the error
        } finally {
            setBusy("");
        }
    };
    const onAgenda = (title) => meeting.agenda.some((item) => topicKey(item) === topicKey(title));
    const togglePresent = (key, checked) => run(`present-${key}`, () => actions.patchMeeting(meeting.key, {
        present: checked ? [...new Set([...meeting.present, key])] : meeting.present.filter((item) => item !== key),
    }));
    const archive = () => confirmDialog({
        group: "project-meetings",
        message: "این جلسه از فهرست حذف می‌شود؛ تصمیم‌های ثبت‌شده‌ی آن باقی می‌مانند.",
        header: "حذف جلسه",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "حذف جلسه",
        rejectLabel: "انصراف",
        acceptClassName: "p-button-danger",
        className: "rtl",
        accept: () => actions.archiveMeeting(meeting.key),
    });

    return (
        <Sidebar visible={visible} position="left" onHide={onHide} className="rtl" pt={drawerPt}
                 header={(
                     <div className="min-w-0">
                         <b className="text-sm">{meeting.title}</b>
                         <p className="pd-sub">{`${faDate(meeting.date)} · ${faDigits(meeting.start)} تا ${faDigits(meeting.end)}`}</p>
                     </div>
                 )}>
            <div className="p-4 flex flex-column gap-3" dir="rtl">
                {/* status, time, people */}
                <div className="pd-card pd-card-pad flex flex-column gap-3">
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <StatusPill kind="meeting" status={meeting.status}/>
                        <span className="flex-1"/>
                        {standalone && project?.canOpen && <button type="button" className="pd-btn small" onClick={() => actions.openProject?.(project.documentId)}><Icon icon="mi mi:folder_open" className="text-sm"/>باز کردن در پروژه</button>}
                        {canEdit && <button type="button" className="pd-btn small" onClick={actions.editMeeting}><Icon icon="mi mi:edit" className="text-sm"/>ویرایش جلسه</button>}
                        {tab.permissions.canDelete && <button type="button" className="pd-btn small" onClick={archive} aria-label="حذف جلسه"><Icon icon="mi mi:delete" className="text-sm tone-red"/></button>}
                    </div>
                    <div className="mt-meta">
                        <span><Icon icon="mi mi:calendar_today"/>{faDate(meeting.date)}</span>
                        <span><Icon icon="mi mi:schedule"/>{`${faDigits(meeting.start)} تا ${faDigits(meeting.end)}`}</span>
                        <LocationText text={meeting.location}/>
                    </div>
                    <div className="mt-meta small">
                        <PersonInline tab={tab} personKey={meeting.owner} prefix="مسئول جلسه: " empty="تعیین نشده"/>
                        {standalone && project?.title && <span><Icon icon="mi mi:folder_open"/>{project.title}</span>}
                    </div>
                    {standalone && !canEdit && <p className="pd-tiny text-500 m-0"><Icon icon="mi mi:lock" className="text-xs"/> فقط مسئول جلسه و سازنده‌ی آن می‌توانند اطلاعات جلسه را تغییر دهند.</p>}
                    {meeting.statusReason && ["not_held", "cancelled"].includes(meeting.status) && (
                        <div className="mt-reason"><Icon icon="mi mi:info" className="text-sm"/><span><b>{meeting.status === "cancelled" ? "دلیل لغو: " : "دلیل برگزار نشدن: "}</b>{meeting.statusReason}</span></div>
                    )}
                    {canEdit && (
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <span className="pd-tiny text-500">وضعیت برگزاری:</span>
                            {STATUS_ACTIONS.filter((item) => item.status !== meeting.status).map((item) => (
                                <button key={item.status} type="button" className="pd-btn small" disabled={Boolean(busy)}
                                        onClick={() => (item.ask
                                            ? (setReason(""), setReasonFor(item))
                                            : run("status", () => actions.patchMeeting(meeting.key, {status: item.status})))}>
                                    <Icon icon={`mi mi:${item.icon}`} className={`text-sm tone-${MEETING_STATUS[item.status].tone}`}/>{item.label}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="mt-people-summary">
                        <div><b>{faNum(meeting.attendees.length)}</b><span>دعوت‌شده</span><AvatarStack tab={tab} keys={meeting.attendees}/></div>
                        <div><b>{faNum(meeting.present.length)}</b><span>حاضر</span><AvatarStack tab={tab} keys={meeting.present}/></div>
                    </div>
                </div>

                {/* preparation of an upcoming meeting */}
                {meeting.status === "scheduled" && !standalone && (
                    <section className="mt-prep">
                        <SectionHead icon="pending_actions" title="قبل از جلسه، این موارد باز را ببینید" sub="موضوع حل‌نشده را به دستور جلسه اضافه کنید تا پیگیری گم نشود."/>
                        {unresolved.map((decision) => {
                            const linked = meeting.decisionKeys.includes(decision.key);
                            const note = (decision.blockerStatus === "open" && decision.blocker) || (decision.status === "done" ? "کارها تکمیل شده؛ نتیجه‌ی تصمیم نیاز به بررسی دارد" : "تصمیم هنوز به نتیجه نرسیده است");
                            return (
                                <div key={decision.key} className="mt-prep-row">
                                    <div className="min-w-0">
                                        <b className="text-xs">{decision.title}</b>
                                        <p className="pd-tiny text-500 m-0 mt-1">{note}</p>
                                    </div>
                                    {linked
                                        ? <span className="pd-pill tone-green"><Icon icon="mi mi:check" className="text-xs"/>پیوند شده</span>
                                        : canEdit && <button type="button" className="pd-btn small" disabled={Boolean(busy)} onClick={() => run(`link-${decision.key}`, () => actions.linkDecision(meeting.key, decision.key))}>
                                            <Icon icon="mi mi:add" className="text-sm"/>{onAgenda(decision.title) ? "پیوند تصمیم" : "افزودن و پیوند"}
                                        </button>}
                                </div>
                            );
                        })}
                        {repeated.map((entry) => (
                            <div key={entry.topic} className="mt-prep-row">
                                <div>
                                    <b className="text-xs">{entry.topic}</b>
                                    <p className="pd-tiny text-500 m-0 mt-1">{`در ${faNum(entry.count)} جلسه‌ی برگزارشده تکرار شده`}</p>
                                </div>
                                {canEdit && <button type="button" className="pd-btn small" disabled={Boolean(busy)} onClick={() => run(`topic-${entry.topic}`, () => actions.patchMeeting(meeting.key, {addTopic: entry.topic}))}>
                                    <Icon icon="mi mi:add" className="text-sm"/>افزودن موضوع
                                </button>}
                            </div>
                        ))}
                        {!unresolved.length && !repeated.length && <p className="pd-tiny tone-green m-0"><Icon icon="mi mi:done_all" className="text-xs"/> تصمیم یا نتیجه‌ی حل‌نشده‌ای برای این جلسه وجود ندارد.</p>}
                    </section>
                )}

                <div className="mt-two-col">
                    <section className="pd-card pd-card-pad">
                        <SectionHead icon="format_list_numbered" title="دستور جلسه" sub={`${faNum(meeting.agenda.length)} موضوع`}/>
                        <ol className="mt-agenda">
                            {meeting.agenda.map((item, index) => <li key={`${item}-${index}`}><span className="mt-index">{faNum(index + 1)}</span><span>{item}</span></li>)}
                        </ol>
                        {!meeting.agenda.length && <p className="pd-tiny text-500 m-0">هنوز موضوعی ثبت نشده است.</p>}
                    </section>
                    <section className="pd-card pd-card-pad">
                        <SectionHead icon="group" title="حاضران و دعوت‌شدگان" sub={held ? "حضور هر نفر را ثبت کنید." : "فهرست اعضای دعوت‌شده"}/>
                        <div className="flex flex-column gap-2">
                            {meeting.attendees.map((key) => {
                                const person = personOf(tab, key);
                                const present = meeting.present.includes(key);
                                return (
                                    <div key={key} className="mt-attendee">
                                        {person?.member && <Avatar memberData={person.member} size="normal"/>}
                                        <span className="text-xs font-bold flex-1">{person?.name || "عضو سابق پروژه"}</span>
                                        {held
                                            ? <label className={`mt-present ${present ? "is-present" : ""}`}>
                                                <Checkbox checked={present} disabled={!canEdit || Boolean(busy)} onChange={(event) => togglePresent(key, event.checked)} aria-label={`حضور ${person?.name || ""}`}/>
                                                <span>{present ? "حاضر" : "غایب"}</span>
                                            </label>
                                            : <span className="pd-tiny text-500">دعوت شده</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </div>

                {held && (
                    <section className="pd-card pd-card-pad">
                        <SectionHead icon="description" title="صورت‌جلسه و بحث اصلی" sub="خلاصه‌ی گفت‌وگوها و جمع‌بندی"
                                     action={canEdit && !editingNotes && <button type="button" className="pd-btn small" onClick={() => {setNotes(meeting.notes); setEditingNotes(true);}}><Icon icon="mi mi:edit_note" className="text-sm"/>ویرایش صورت‌جلسه</button>}/>
                        {editingNotes ? (
                            <div className="flex flex-column gap-2">
                                <RichTextEditor value={notes} onChange={setNotes} placeholder="بحث‌های اصلی و جمع‌بندی جلسه را ثبت کنید…" minHeight="7rem" autoFocus ariaLabel="صورت‌جلسه"/>
                                <div className="flex justify-content-end gap-2">
                                    <button type="button" className="pd-btn small" onClick={() => setEditingNotes(false)}>انصراف</button>
                                    <button type="button" className="pd-btn small primary" disabled={Boolean(busy)}
                                            onClick={() => run("notes", async () => {await actions.patchMeeting(meeting.key, {notes}); setEditingNotes(false);})}>ذخیره صورت‌جلسه</button>
                                </div>
                            </div>
                        ) : <RichTextView value={meeting.notes} className="text-xs text-700" empty={<p className="pd-tiny text-500 m-0">هنوز متنی برای صورت‌جلسه ثبت نشده است.</p>}/>}
                        <p className="pd-tiny text-500 m-0 mt-2">صورت‌جلسه به‌خودی‌خود تصمیم یا کار نمی‌سازد؛ خروجی قابل پیگیری را جداگانه ثبت کنید.</p>
                    </section>
                )}

                {standalone && brief.length > 0 && (
                    <section className="pd-card pd-card-pad">
                        <SectionHead icon="done_all" title="تصمیم‌های این جلسه" sub="برای پیگیری کامل، پروژه را باز کنید."/>
                        <div className="flex flex-column gap-2">
                            {brief.map((decision) => (
                                <div key={decision.key} className="mt-prep-row">
                                    <div className="min-w-0">
                                        <b className="text-xs">{decision.title}</b>
                                        <p className="pd-tiny text-500 m-0 mt-1">{`${personOf(tab, decision.owner)?.name || "بدون مسئول"}${decision.dueDate ? ` · موعد ${faShortDate(decision.dueDate)}` : ""}`}</p>
                                    </div>
                                    <StatusPill kind="decision" status={decision.status}/>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {!standalone && <section className="pd-card pd-card-pad">
                    <SectionHead icon="done_all" title="تصمیم‌ها و کارهای حاصل" sub="تصمیم انتخاب انجام‌شده است؛ کار اقدام اجرایی است؛ نتیجه جداگانه تأیید می‌شود."
                                 action={held && canEdit && <button type="button" className="pd-btn small primary" onClick={actions.recordOutput}><Icon icon="mi mi:add" className="text-sm"/>ثبت خروجی جلسه</button>}/>
                    {decisions.length ? (
                        <div className="flex flex-column gap-3">
                            {decisions.map((decision) => <OutputCard key={decision.key} decision={decision} meeting={meeting} tab={tab} openDecision={actions.openDecision}/>)}
                        </div>
                    ) : (
                        <div className="mt-no-output">
                            <Icon icon="mi mi:error_outline" className="tone-amber"/>
                            <div className="flex-1">
                                <b className="text-xs">{held ? "خروجی این جلسه ثبت نشده" : meeting.status === "scheduled" ? "جلسه هنوز برگزار نشده است" : meeting.status === "cancelled" ? "این جلسه لغو شده است" : "این جلسه برگزار نشده است"}</b>
                                <p className="pd-tiny text-500 m-0 mt-1">{held ? "تصمیم یا تعهدی را ثبت کنید تا قدم بعدی و مسئول آن مشخص شود." : meeting.status === "scheduled" ? "بعد از برگزاری، وضعیت را «برگزار شد» کنید تا حضور و خروجی ثبت شود." : "این وضعیت را با جلسه‌ی برگزارشده یکی نگیرید."}</p>
                            </div>
                            {held && canEdit && <button type="button" className="pd-btn small primary" onClick={actions.recordOutput}>ثبت اولین خروجی</button>}
                        </div>
                    )}
                </section>}
                {!standalone && <p className="pd-tiny text-500 m-0"><Icon icon="mi mi:info" className="text-xs"/> تکمیل کارها به‌تنهایی اثبات نمی‌کند که نتیجه‌ی مورد انتظار تصمیم حاصل شده است.</p>}
            </div>
            <Dialog visible={Boolean(reasonFor)} onHide={() => setReasonFor(null)} modal dir="rtl" className="rtl w-11 md:w-6 lg:w-4"
                    header={<b className="text-sm">{reasonFor?.ask}</b>}
                    footer={(
                        <div className="flex justify-content-end gap-2">
                            <button type="button" className="pd-btn" onClick={() => setReasonFor(null)}>انصراف</button>
                            <button type="button" className="pd-btn primary" disabled={!reason.trim() || Boolean(busy)}
                                    onClick={() => run("status", async () => {
                                        await actions.patchMeeting(meeting.key, {status: reasonFor.status, reason: reason.trim()});
                                        setReasonFor(null);
                                    })}>{`تأیید و ثبت «${reasonFor?.label || ""}»`}</button>
                        </div>
                    )}>
                <div className="flex flex-column gap-2">
                    <InputTextarea autoFocus value={reason} onChange={(event) => setReason(event.target.value)} rows={3} autoResize maxLength={1000}
                                   placeholder="مثلاً مسئول جلسه در مأموریت بود؛ به هفته‌ی بعد موکول شد." className="w-full text-xs"/>
                    <p className="pd-tiny text-500 m-0">دلیل در پرونده‌ی جلسه می‌ماند و برای همه‌ی دعوت‌شدگان دیده می‌شود.</p>
                </div>
            </Dialog>
        </Sidebar>
    );
}

function OutputCard({decision, meeting, tab, openDecision}) {
    const tasks = decision.taskKeys.map((key) => tab.tasks.find((task) => task.key === key)).filter(Boolean);
    const target = decision.target ? tab.targets.find((item) => item.type === decision.target.type && item.id === decision.target.id) : null;
    const progress = doneShare(tasks);
    return (
        <article className="mt-output-card">
            <div className="flex align-items-start gap-2">
                <div className="flex-1 min-w-0">
                    <span className="pd-tiny text-500 block">{decision.meetingKey === meeting.key ? "تصمیم مبدأ" : "تصمیم پیگیری"}</span>
                    <button type="button" className="mt-title-link" onClick={() => openDecision(decision.key)}>{decision.title}<Icon icon="mi mi:chevron_left" className="text-sm"/></button>
                </div>
                <StatusPill kind="decision" status={decision.status}/>
            </div>
            <div className="mt-meta small">
                <PersonInline tab={tab} personKey={decision.owner} prefix="مسئول: "/>
                <span><Icon icon="mi mi:event"/>{`موعد: ${faShortDate(decision.dueDate)}`}</span>
                {target && <span><Icon icon="mi mi:flag"/>{target.title}</span>}
            </div>
            {decision.blocker && decision.blockerStatus !== "resolved" && <div className="mt-blocker"><Icon icon="mi mi:block" className="text-sm"/><span className="flex-1">{decision.blocker}</span></div>}
            <Chain items={[{label: "جلسه", done: true}, {label: "تصمیم", done: true}, {label: tasks.length ? `${faNum(tasks.length)} کار` : "بدون کار", done: tasks.length > 0 && progress === 100}, {label: decision.resultVerified ? "نتیجه تأیید شد" : "نتیجه ثبت/تأیید نشده", done: decision.resultVerified}]}/>
            {tasks.length ? (
                <div className="flex flex-column gap-1">
                    {tasks.map((task) => (
                        <button key={task.key} type="button" className="mt-task-row" onClick={() => openDecision(decision.key, task.key)}>
                            <Icon icon={task.status === "done" ? "mi mi:check_circle" : "mi mi:radio_button_unchecked"} className={task.status === "done" ? "tone-green" : "text-400"}/>
                            <span className="flex-1 min-w-0 text-right">
                                <b className="text-xs block text-overflow-ellipsis white-space-nowrap overflow-hidden">{task.title}</b>
                                <span className="pd-tiny text-500">{`${personOf(tab, task.assignee)?.name || "بدون مسئول"} · موعد ${faShortDate(task.dueDate)}`}</span>
                            </span>
                            <StatusPill kind="task" status={task.status}/>
                        </button>
                    ))}
                    <div className="flex align-items-center gap-2 pd-tiny text-500"><span>کارهای انجام‌شده</span><Progress value={progress}/></div>
                </div>
            ) : <p className="pd-tiny text-500 m-0">برای این تصمیم هنوز کار اجرایی مرتبطی تعریف نشده است.</p>}
            <button type="button" className="mt-result-strip" onClick={() => openDecision(decision.key)}>
                <Icon icon={decision.resultVerified ? "mi mi:verified" : "mi mi:hourglass_empty"} className={decision.resultVerified ? "tone-green" : "tone-amber"}/>
                <span className="flex-1 text-right">
                    <b className="text-xs block">{decision.resultVerified ? "نتیجه‌ی تصمیم تأیید شده" : decision.status === "done" ? "کارها تکمیل شده؛ نتیجه نیازمند بررسی است" : "نتیجه‌ی نهایی هنوز ثبت نشده"}</b>
                    <span className="pd-tiny text-500">{decision.result ? "نتیجه ثبت شده است" : "نتیجه را پس از مشاهده‌ی اثر واقعی ثبت کنید."}</span>
                </span>
                <span className="pd-link pd-tiny">جزئیات و پیگیری</span>
            </button>
        </article>
    );
}
